import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { AnnounceDialogComponent, LocationAndOrdertypeDialogComponent, WarningdialogComponent } from "../modalDialogs";
import { BenefitRedemptionStatus, OrdereRequirementsStatus, AnounceModalResult, RedeemType } from "../shared/contentTypes";
import { BottomSheetHelperService } from "./bottomSheetHelper.service";
import { IBenefitCacheEntry, WebShopMode } from "./IWebShopService";
import { WebShopService } from "./webShop.service";
import { BonusactionDetailViewModel, BonusActionViewModel, ProductViewModel, ScheduledForRequest, BasketItemViewModel, BonusactionBenefitViewModel } from "../shared/models";
import { CoreLogService } from "./log.service";
import { CoreCOMService } from "./com.service";
import Utils from "../shared/utils";
import { LocalKeys, StorageHive } from "./valueStorage.service";
import * as moment from "moment";
import { ProductService } from "./product.service";
import { CoreLanguageService } from "./language.service";

/**
 * Validation result for a benefit item with not enough points
 */
export interface BenefitConsumedPointsValidation {
  basketItemId: number | null;
  hasEnoughPoints: boolean;
  bonusActionId?: number;
  requiredPoints?: number;
  availablePoints?: number;
  usedPoints?: number;
}

/**
 * Service for handling bonus benefit redemption logic
 * Manages the redemption flow, validation, and dialogs
 */
@Injectable({
  providedIn: 'root'
})
export class BenefitRedemptionService {

  constructor(
    private _webShopService: WebShopService,
    private _bottomSheetHelperService: BottomSheetHelperService,
    private _router: Router,
    private _coreLogService: CoreLogService,
    private _coreComService: CoreCOMService,
    private _productService: ProductService,
    private _coreLanguageService: CoreLanguageService,
  ) { }

  //#region Main Redemption Flow

  /**
   * Starts the benefit redemption process with full validation
   * @param productId Benefit product ID
   * @param bonusLocationIds IDs of participating locations
   * @param benefitData Benefit cache entry data
   */
  public async startRedeemProcess(productId: number, bonusLocationIds: number[], benefitData: IBenefitCacheEntry): Promise<void> {
    const me = this;
    const serviceMode = me._webShopService.getServiceMode();
    const locationId = me._webShopService.locationID;
    if (!locationId) {
      me._openSelectLocationAndOrderType(productId, bonusLocationIds, benefitData);
      return;
    }
    if (!me._isOrderModeValid(serviceMode)) {
      me._openOrderTypeNotValidDialog();
      return;
    }
    const orderRequirementStatus = await me._webShopService.checkRequiredOrderDataStatus();
    if (orderRequirementStatus !== OrdereRequirementsStatus.OK) {
      await me._processMissingOrderRequirements(productId);
      return;
    }
    const state = await me.checkBenefitStateAndRedeemPoints(productId, true, -1);
    if (state === BenefitRedemptionStatus.OK) {
      me.cacheBenefitData(benefitData)
    }
    me.checkStateAndOpenDialogIfNeed(state);
  }

  /**
    * @param product_id benefit product id
    * @param with_navigation whether to navigate on success
    * @param returnItemGroupId item group ID on navigation back
    * @returns BenefitRedemptionStatus
    */
  private async redeemBonusPoints(product_id: number, with_navigation: boolean = true, returnItemGroupId: number): Promise<BenefitRedemptionStatus> {
    const me = this;
    const webshopSettings = await me._webShopService.getWebshopSettingsDataModel();
    const locationID = me._webShopService.locationID;
    const products = await me._productService.GetProductsByFilter(locationID, me._webShopService.sortiment, (product) => product.product_id == product_id && product.saleable);
    if (products.length === 0) {
      me._coreLogService.info("BenefitRedemptionService.navigateToBenefitProduct", "unknown product");
      return BenefitRedemptionStatus.NoProduct;
    }
    const itemTags = [];
    const likeItemTagIds = [];
    const schedule_req = new ScheduledForRequest(me._webShopService.getScheduledForReferenceTime(), locationID, webshopSettings, me._coreLanguageService);
    const product = new ProductViewModel(locationID, products[0], itemTags, likeItemTagIds, schedule_req);
    if (product.availible) {
      if (with_navigation) {
        let success_navigation = false;
        if (product.is_recipe_linking) {
          success_navigation = await me._router.navigate(["recipeLink", returnItemGroupId, product.id, locationID]);
        } else {
          success_navigation = await me._router.navigate(["menudetail", returnItemGroupId, product.id, locationID]);
        }
        if (success_navigation) {
          return BenefitRedemptionStatus.OK;
        } else {
          me._coreLogService.error("BenefitRedemptionService.redeemBonusPoints", "navigation faild");
        }
      } else {
        return BenefitRedemptionStatus.OK;
      }
    } else {
      return BenefitRedemptionStatus.NotAvailable;
    }
  }

  /**
   * Checks benefit state and redeems points
   * @param product_id Product ID
   * @param with_navigation Whether to navigate on success
   * @param returnItemGroupId Item group ID to return to
   */
  public async checkBenefitStateAndRedeemPoints(product_id: number, with_navigation: boolean = true, returnItemGroupId: number) {
    const me = this;
    const state = await me.redeemBonusPoints(product_id, with_navigation, returnItemGroupId);
    return state;
  }

  /**
   * Displays appropriate dialog based on benefit redemption state
   * @param state Benefit redemption status
   */
  public checkStateAndOpenDialogIfNeed(state: BenefitRedemptionStatus): void {
    const me = this;
    if (state !== BenefitRedemptionStatus.OK) {
      if (state === BenefitRedemptionStatus.NoProduct) {
        me._openWarningDialog("phBenefit_Redeem_No_Product_Dialog_Title", "phBenefit_Redeem_No_Product_Dialog_Info", "phGlobal_Continue", false);
      } else if (state === BenefitRedemptionStatus.NotAvailable) {
        me._openWarningDialog("phBenefit_Redeem_Product_NotAvailable_Dialog_Title", "phBenefit_Redeem_Product_NotAvailable_Dialog_Info", "phGlobal_Continue", false);
      }
      me.clearCachedBenefitData();
    }
  }

  //#endregion

  //#region Basket Operations

  /**
   * Retrieves all benefits currently in the basket
   * @returns Array of benefit cache entries
   */
  private getBasketBenefitData(): IBenefitCacheEntry[] {
    const me = this;
    const basketItems = me._webShopService.getShippingCartItems();
    let basketBenefitData: IBenefitCacheEntry[] = [];

    if (basketItems && basketItems.length > 0) {
      basketItems.forEach(item => {
        const hasBenefitId = item.benefitId && item.benefitId > 0;
        const hasBonusActionId = item.bonusactionId && item.bonusactionId > 0;
        if (item && hasBenefitId && hasBonusActionId) {
          basketBenefitData.push({
            bonusactionId: item.bonusactionId,
            benefitId: item.benefitId,
            benefitProductId: item.benefitProductId,
            benefitLossId: item.benefitLossId,
            benefitCostPoints: item.benefitCostPoints
          });
        }
      });
    }
    return basketBenefitData;
  }

  /**
   * Get all benefit items from shopping cart
   * @returns Array of basket items that are benefits
   */
  private getBenefitItemsFromBasket(): BasketItemViewModel[] {
    const basketItems = this._webShopService.getShippingCartItems();
    if (!basketItems || basketItems.length === 0) {
      return [];
    }

    return basketItems.filter((item) => {
      return item.bonusactionId && item.bonusactionId > 0 &&
        item.benefitCostPoints && item.benefitCostPoints > 0;
    });
  }

  /**
   * Check if benefit products are in basket and updates isInBasket flag
   * @param bonusAction Single bonus action
   */
  public updateBenefitBasketStatus(bonusAction: BonusActionViewModel | BonusactionDetailViewModel): void {
    const me = this;
    const basketBenefitData = me.getBasketBenefitData();

    if (!basketBenefitData || basketBenefitData.length === 0) {
      bonusAction.pointsInBasket = 0;
      return;
    }
    bonusAction.benefits.forEach(benefit => {
      const isInBasket = basketBenefitData.some(data =>
        data.benefitId === benefit.id &&
        data.bonusactionId === benefit.bonusaction_id
      );
      benefit.isInBasket = isInBasket;
    });
    bonusAction.pointsInBasket = me.getpointsInBasket(bonusAction);
  }

  /**
   * Checks if benefit products are in basket and Updates isInBasket flag for each benefit
   * @param bonusActions Array of bonus action
   */
  public updateBenefitBasketStatusForList(bonusActions: BonusActionViewModel[]): void {
    const me = this;
    const basketBenefitData = me.getBasketBenefitData();

    if (!basketBenefitData || basketBenefitData.length === 0) {
      bonusActions.forEach(bonus => bonus.pointsInBasket = 0);
      return;
    }

    bonusActions.forEach(bonus => {
      bonus.benefits.forEach(benefit => {
        const isInBasket = basketBenefitData.some(data =>
          data.benefitId === benefit.id &&
          data.bonusactionId === benefit.bonusaction_id &&
          data.benefitLossId === benefit.loss_id
        );
        benefit.isInBasket = isInBasket;
      });
      bonus.pointsInBasket = me.getpointsInBasket(bonus);
    });
  }

  //#endregion

  //#region Points Calculations


  /**
   * Calculates total points in basket for a bonus action
   * @param bonus Bonus action or detail view model
   * @returns Total points amount
   */
  private getpointsInBasket(bonus: BonusActionViewModel | BonusactionDetailViewModel): number {
    const me = this;
    const basketItems = me._webShopService.getShippingCartItems();

    if (!basketItems || basketItems.length === 0) {
      return 0;
    }
    let totalPoints = 0;
    bonus.benefits.filter(i => i.isInBasket && i.bonusaction_id === bonus.id).forEach(benefit => {
      const matchingBasketItems = basketItems.filter(item =>
        item.benefitId === benefit.id &&
        item.bonusactionId === benefit.bonusaction_id &&
        item.benefitLossId === benefit.loss_id
      );
      matchingBasketItems.forEach(basketItem => {
        totalPoints += benefit.cost * basketItem.count;
      });
    });

    return totalPoints;
  }

  /**
   * Checks if user can redeem a benefit again after current basket purchase
   * @param currentPoints User's current bonus points
   * @param pointsInBasket Points already in basket
   * @param benefitCost Cost of the benefit
   * @returns True if user has enough remaining points
   */
  public canRedeemAgain(currentPoints: number, pointsInBasket: number, benefitCost: number): boolean {
    const remainingPoints = currentPoints - pointsInBasket;
    return remainingPoints >= benefitCost;
  }

  /**
 * Calculates percentage of points remaining after basket purchase relative to benefit cost
 * @param benefitCost Cost of the benefit
 * @param currentPoints User's current points
 * @param pointsInBasket Points in basket
 * @returns Percentage (0-100)
 */
  public benefitInBasketPercentage(benefitCost: number, currentPoints: number, pointsInBasket: number): number {
    const remainingPoints = currentPoints - pointsInBasket;
    return this.calculateBenefitPercentage(remainingPoints, benefitCost);
  }

  /**
   * Calculates the benefit percentage of user points relative to benefit cost.
   * @returns Percentage
   */
  public calculateBenefitPercentage(currentPoints: number, benefitCost: number): number {
    if (!benefitCost || benefitCost <= 0) {
      return 0;
    }
    return Math.floor((currentPoints * 100) / benefitCost);
  }

  /**
   * Recalculates benefitpercentage for every benefit of a bonus action based
   * on the action's current points.
   */
  public recalculateBenefitPercentages(bonusAction: BonusActionViewModel | BonusactionDetailViewModel): void {
    const me = this;
    if (!bonusAction || !bonusAction.benefits) {
      return;
    }
    bonusAction.benefits.forEach(benefit => {
      benefit.benefitpercantage = me.calculateBenefitPercentage(bonusAction.points, benefit.cost);
    });
  }

  /** Recalculates benefitpercentage for a list of bonus actions */
  public recalculateBenefitPercentagesForList(bonusActions: BonusActionViewModel[]): void {
    const me = this;
    if (!bonusActions) {
      return;
    }
    bonusActions.forEach(action => me.recalculateBenefitPercentages(action));
  }

  //#endregion

  //#region UI Helpers & Formatting

  /**Show hint if there are points in basket*/
  public showPointsInBasketLabel(pointsInBasket: number) {
    return pointsInBasket > 0;
  }

  /** Hint about costs(points) for the benefit products in basket */
  public pointsInBasketLabel(currentPoints: number, benefitCostinBasket: number) {
    const me = this;
    return me._coreLanguageService.value("phBonusAction_Costs_inBasket_Points")
      .replace("{currentPoints}", `<span class="emphasized">${currentPoints.toString()}</span>`)
      .replace("{benefitCosts}", `<span class="emphasized">${benefitCostinBasket.toString()}</span>`);
  }
  //#endregion

  //#region Validation

  public async canRedeemBenefitOnline(): Promise<boolean> {
    const me = this;
    const settings = await me._webShopService.getWebshopSettingsDataModel();
    return settings && settings.allow_benefits_in_webshop;
  }

  /**
   * Validates if the order mode is valid for benefit redemption
   * @param orderMode Order mode to validate
   * @returns True if valid
   */
  private _isOrderModeValid(orderMode: number): boolean {
    if (!orderMode) return false;
    return (orderMode === WebShopMode.Delivery ||
      orderMode === WebShopMode.TakeAway ||
      orderMode === WebShopMode.Retail);
  }

  /**
 * Check if the user has enough points to redeem a single benefit item.
 * @param item - The basket item to validate.
 * @param benefitCost - The cost of the  clicked benefit.
 * @param userPointsForAction - The user's available points for the specific action.
 * @param pointsConsumed - Points already consumed for the action.
 * @returns - A validation object with the necessary points details.
 */
  private checkIfEnoughPointsForItem(item: BasketItemViewModel, benefitCost: number, userBonusPoints: number, pointsConsumed: number): BenefitConsumedPointsValidation {
    const actionId = item.bonusactionId;
    return {
      basketItemId: item.id,
      hasEnoughPoints: (userBonusPoints - pointsConsumed) >= benefitCost,
      bonusActionId: actionId,
      requiredPoints: userBonusPoints - pointsConsumed,
      availablePoints: userBonusPoints - pointsConsumed,
      usedPoints: pointsConsumed
    };
  }


  /**
 * Checks if the user can redeem additional benefits from the current action.
 * @param benefitCost - The cost of clicked benefit.
 * @param userBonusPoints - The total available bonus points for the user.
 * @returns - A BenefitPointsValidation object for the first item that cannot be redeemed or null .
 */
  public validateConsumedBenefitPoints(benefitCost: number, userBonusPoints: number, pointsConsumed: number): BenefitConsumedPointsValidation | null {
    const me = this;
    const benefitBasketItems = me.getBenefitItemsFromBasket();
    if (benefitBasketItems.length === 0 || (!userBonusPoints || userBonusPoints <= 0)) {
      return null;
    }
    const pointsConsumedPerAction: { [actionId: number]: number } = {};

    for (let item of benefitBasketItems) {
      const consumedPointsValidation = me.checkIfEnoughPointsForItem(item, benefitCost, userBonusPoints, pointsConsumed);

      if (!consumedPointsValidation.hasEnoughPoints) {
        return consumedPointsValidation;
      }
      const actionId = item.bonusactionId;
      const requiredPoints = item.benefitCostPoints * (item.count || 1);
      pointsConsumedPerAction[actionId] = (pointsConsumedPerAction[actionId] || 0) + requiredPoints;
    }
    return null;
  }

  public handleConsumedPointsValidation(benefitCost: number, userBonusPoints: number, pointsConsumed: number): boolean {
    const me = this;
    const consumedPointsValidation = me.validateConsumedBenefitPoints(benefitCost, userBonusPoints, pointsConsumed);
    if (consumedPointsValidation && consumedPointsValidation !== null) {
      const availablePoints = userBonusPoints - pointsConsumed;
      me.openWarningNotEnoughPoints(pointsConsumed, availablePoints);
      return false;
    } else {
      return true;
    }
  }

  //#endregion

  //#region Dialogs

  /**
   * Opens dialog when order type is not valid for benefit redemption
   */
  private _openOrderTypeNotValidDialog(): void {
    const dialogRef = this._bottomSheetHelperService.open(AnnounceDialogComponent, {
      data: {
        title: "phBenefit_Redeem_OrderType_Not_Valid_Dialog_Title",
        content: "phBenefit_Redeem_OrderType_Not_Valid_Dialog_SubText",
        firstBtnLabel: "phBenefit_Redeem_OrderType_Not_Valid_Dialog_Change_Btn",
        secondBtnLabel: "phBenefit_Redeem_OrderType_Not_Valid_Dialog_Cancel_Btn"
      }
    });

    dialogRef.afterDismissed().subscribe((result: AnounceModalResult) => {
      if (result === AnounceModalResult.FirstButton) {
        this._router.navigate(["/menuservice"]);
      }
    });
  }

  /**
   * Opens dialog to select location and order type
   * @param productId Product ID
   * @param bonusFilterLocationIds Available location IDs
   */
  private _openSelectLocationAndOrderType(productId: number, bonusFilterLocationIds: number[], benefitData: IBenefitCacheEntry): void {
    const me = this;
    const dialogRef = me._bottomSheetHelperService.open(LocationAndOrdertypeDialogComponent, {
      data: {
        locationIds: bonusFilterLocationIds,
        redeemType: RedeemType.Benefit
      }
    });
    dialogRef.afterDismissed().subscribe(async (result) => {
      if (!result || !result.selectedLocation || !result.selectedServiceMode) return;
      if (result.selectedServiceMode === WebShopMode.OrderToTable) {
        me._openWarningDialog(
          "phBonus_Redeem_Table_Login_Required_Title",
          "phBonus_Redeem_Table_Login_Required_info",
          "phGlobal_Continue",
          true
        );
        return;
      }
      await me._initializeSelection(result.selectedServiceMode, result.selectedLocation);
      const orderRequirementsStatus = await me._webShopService.checkRequiredOrderDataStatus();
      const state = await me.checkBenefitStateAndRedeemPoints(productId, false, -1);

      if (orderRequirementsStatus !== OrdereRequirementsStatus.OK && state === BenefitRedemptionStatus.OK) {
        await me.cacheBenefitData(benefitData);
        me.navigateToOrderRequirement();
      } else {
        me.checkStateAndOpenDialogIfNeed(state);
      }
    });
  }

  /**
   * Opens warning dialog with optional navigation
   * @param title Dialog title translation key
   * @param content Dialog content translation key
   * @param buttonLabel Button label translation key
   * @param withNavigation Whether to navigate after dismissal
   */
  private _openWarningDialog(title: string, content: string, buttonLabel: string, withNavigation: boolean): void {
    const me = this;
    const warningButtomSheet = me._bottomSheetHelperService.open(WarningdialogComponent, {
      data: {
        title: title,
        content: content,
        btnLabel: buttonLabel
      },
      panelClass: "mat-bottom-sheet-container-medium"
    });

    if (withNavigation) {
      warningButtomSheet.afterDismissed().subscribe(async (result) => {
        if (result) {
          await me._webShopService.setServiceMode(WebShopMode.OrderToTable);
          me._router.navigate(["/qrcodescanner"]);
        }
      });
    }
  }

  private notEnoughPointsInfo(consumedPoints: number, availablePoints: number) {
    return this._coreLanguageService.value("phBenefit_Redeem_Not_Enough_PointsDialog_SubText")
      .replace("{consumedPoints}", `<span class="emphasized">${consumedPoints}</span>`)
      .replace("{availablePoints}", `<span class="emphasized">${availablePoints}</span>`);
  }

  private openWarningNotEnoughPoints(consumedPoints: number, availablePoints: number) {
    const me = this;
    me._bottomSheetHelperService.open(WarningdialogComponent, {
      data: {
        title: "phBenefit_Redeem_Not_Enough_Points_Dialog_Title",
        content: me.notEnoughPointsInfo(consumedPoints, availablePoints),
        btnLabel: "phGlobal_Continue",
        emphasizedText: true
      },
      panelClass: "mat-bottom-sheet-container-medium",
    });
  }

  //#endregion

  //#region Navigation

  /**
   * Navigates to the appropriate order requirement page
   */
  private async navigateToOrderRequirement(): Promise<void> {
    const me = this;
    if (me._webShopService.getServiceMode()) {
      const status = await me._webShopService.checkRequiredOrderDataStatus();
      if (status) {
        if (status === OrdereRequirementsStatus.TimeRequired) {
          me._router.navigate(["/orderTimeSelector"]);
        } else if (status === OrdereRequirementsStatus.AddressRequired) {
          me._router.navigate(["/orderAddressQuery"]);
        }
      }
    }
  }

  /**
   * Processes missing order requirements and navigates accordingly
   * @param productId Product ID
   */
  private async _processMissingOrderRequirements(productId: number): Promise<void> {
    const me = this
    const state = await me.checkBenefitStateAndRedeemPoints(productId, true, -1);
    if (state !== BenefitRedemptionStatus.OK) {
      me.checkStateAndOpenDialogIfNeed(state);
      return;
    }
    me.navigateToOrderRequirement();
  }

  //#endregion

  //#region Data Management

  /**
   * Initializes service mode and location selection
   * @param serviceMode Service mode to set
   * @param locationID Location ID to set
   */
  private async _initializeSelection(serviceMode: WebShopMode, locationID: number): Promise<void> {
    await this._webShopService.setServiceMode(serviceMode);
    await this._webShopService.setServiceLocationByID(+locationID);
  }

  /**
   * Creates a benefit cache entry from a benefit view model
   * @param benefit Benefit view model to convert
   * @returns Benefit cache entry
   */
  public createBenefitCacheEntry(benefit: BonusactionBenefitViewModel): IBenefitCacheEntry {
    return {
      bonusactionId: benefit.bonusaction_id,
      benefitId: benefit.id,
      benefitProductId: benefit.product_id,
      benefitLossId: benefit.loss_id,
      benefitCostPoints: benefit.cost
    };
  }

  //#endregion

  //#region Cache
  /**
   * Caches benefit data to local storage
   * @param benefitCacheEntry Benefit cache entry data
   */
  private async cacheBenefitData(benefitCacheEntry: IBenefitCacheEntry): Promise<void> {
    const me = this;
    try {
      const now = moment();
      const cachedData = {
        bonusactionId: benefitCacheEntry.bonusactionId,
        benefitId: benefitCacheEntry.benefitId,
        benefitProductId: benefitCacheEntry.benefitProductId,
        benefitLossId: benefitCacheEntry.benefitLossId,
        benefitCostPoints: benefitCacheEntry.benefitCostPoints,
        cachedAt_YMD: Utils.GetYMDFromMoment(now),
        cachedAt_HMS: Utils.GetHMSFromMoment(now),
      };
      await me._coreComService.environment.valueStorage.set(StorageHive.Local, LocalKeys.benefitCache, cachedData);
    } catch (error) {
      me._coreLogService.error("WebShopService.setCachedBenefitData", error);
    }
  }

  /**
   * Clear benefit cache
   */
  public async clearCachedBenefitData(): Promise<void> {
    const me = this;
    try {
      await me._coreComService.environment.valueStorage.remove(StorageHive.Local, LocalKeys.benefitCache);
    } catch (error) {
      me._coreLogService.error("WebShopService.clearCachedBenefitData", error);
    }
  }

  /**
 * Get cached benefit data
 * @returns Cached benefit data with actionId, benefitId, productId or null
 */
  public async getCachedBenefitData(): Promise<IBenefitCacheEntry | null> {
    const me = this;
    try {
      const benefitCachedData: IBenefitCacheEntry = await me._coreComService.environment.valueStorage.get(StorageHive.Local, LocalKeys.benefitCache);
      if (benefitCachedData) {
        return benefitCachedData;
      }
      return null;
    } catch (error) {
      me._coreLogService.error("WebShopService.getCachedBenefitData", error);
      return null;
    }
  }

  //#endregion
}
